import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  WalletIcon,
  MagnifyingGlassIcon,
  CurrencyDollarIcon,
  TrophyIcon,
  ArrowRightIcon,
  ChartBarIcon,
  ArrowTrendingDownIcon,
  EyeSlashIcon,
  FireIcon,
  ClockIcon,
  ShieldCheckIcon,
  SparklesIcon,
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';

const auctionTypes = [
  {
    id: 'english',
    name: 'English Auction',
    nameEs: 'Subasta Inglesa',
    icon: ChartBarIcon,
    color: 'from-green-500 to-emerald-600',
    description: 'La subasta tradicional donde los participantes pujan cada vez más alto.',
    howItWorks: [
      'El vendedor establece un precio de salida',
      'Los compradores pujan incrementando el precio',
      'Cada nueva puja debe superar la anterior',
      'Al finalizar el tiempo, gana la puja más alta',
    ],
    pros: ['Transparente', 'Maximiza el precio para vendedores', 'Emocionante para participantes'],
    cons: ['Puede provocar "fiebre de subasta"', 'Requiere atención constante'],
    bestFor: 'Items únicos con alta demanda donde los compradores compiten abiertamente.',
    example: 'Un NFT raro con precio de salida de 5 ETH puede terminar en 15 ETH si hay mucho interés.',
  },
  {
    id: 'dutch',
    name: 'Dutch Auction',
    nameEs: 'Subasta Holandesa',
    icon: ArrowTrendingDownIcon,
    color: 'from-orange-500 to-red-600',
    description: 'El precio comienza alto y va bajando hasta que alguien compra.',
    howItWorks: [
      'El vendedor establece un precio inicial alto',
      'El precio baja automáticamente con el tiempo',
      'El primer comprador que acepta el precio gana',
      'La subasta termina inmediatamente tras la compra',
    ],
    pros: ['Rápida resolución', 'Precio justo de mercado', 'Sin guerras de pujas'],
    cons: ['Riesgo de comprar "demasiado pronto"', 'Menos emocionante'],
    bestFor: 'Ventas rápidas donde el vendedor quiere garantizar una venta a precio de mercado.',
    example: 'Un NFT empieza en 10 ETH y baja 0.5 ETH cada hora. Si alguien compra a 7 ETH, la subasta termina.',
  },
  {
    id: 'vickrey',
    name: 'Vickrey Auction',
    nameEs: 'Subasta Vickrey',
    icon: EyeSlashIcon,
    color: 'from-purple-500 to-indigo-600',
    description: 'Pujas selladas donde el ganador paga el precio de la segunda puja más alta.',
    howItWorks: [
      'Los participantes envían pujas selladas (privadas)',
      'Nadie ve las pujas de los demás',
      'Al finalizar, se revelan todas las pujas',
      'El ganador paga el precio de la segunda puja más alta',
    ],
    pros: ['Incentiva pujas honestas', 'Sin guerras de precios', 'Justo para compradores'],
    cons: ['Requiere confianza en el sistema', 'Menos transparente durante el proceso'],
    bestFor: 'Items donde se quiere descubrir el verdadero valor de mercado sin manipulación.',
    example: 'Si las pujas son 10, 8 y 6 ETH, el ganador (10 ETH) paga solo 8 ETH.',
  },
  {
    id: 'candle',
    name: 'Candle Auction',
    nameEs: 'Subasta de Vela',
    icon: FireIcon,
    color: 'from-yellow-500 to-orange-600',
    description: 'Similar a la inglesa pero con final aleatorio para evitar pujas de último segundo.',
    howItWorks: [
      'Funciona como una subasta inglesa',
      'El tiempo de finalización exacto es aleatorio',
      'Puede terminar en cualquier momento dentro de un rango',
      'Evita las pujas de último segundo (sniping)',
    ],
    pros: ['Evita "sniping"', 'Más justo para todos', 'Incentiva pujas tempranas'],
    cons: ['Incertidumbre sobre el final', 'Puede terminar antes de lo esperado'],
    bestFor: 'Subastas donde se quiere evitar que compradores esperen al último segundo.',
    example: 'La subasta puede terminar entre las 3:00 y las 4:00 PM. El momento exacto es secreto.',
  },
];

const steps = [
  {
    number: 1,
    title: 'Conecta tu Wallet',
    description: 'Conecta tu wallet de Polygon (MetaMask, WalletConnect, etc.) para acceder a todas las funciones.',
    icon: WalletIcon,
  },
  {
    number: 2,
    title: 'Explora y Encuentra',
    description: 'Navega por la galería, filtra por categoría, precio o tipo de subasta. Guarda items en favoritos.',
    icon: MagnifyingGlassIcon,
  },
  {
    number: 3,
    title: 'Participa en Subastas',
    description: 'Haz tus pujas según el tipo de subasta. Cada tipo tiene sus propias reglas y estrategias.',
    icon: CurrencyDollarIcon,
  },
  {
    number: 4,
    title: 'Gana y Recibe',
    description: 'Si ganas, el NFT se transfiere automáticamente a tu wallet. Recibirás una factura de la compra.',
    icon: TrophyIcon,
  },
];

const faqs = [
  {
    question: '¿Qué es NDH y por qué lo necesito?',
    answer: 'NDH es el token nativo del ecosistema NARDIHA. Aunque puedes pujar con ETH, stakear NDH te da descuentos en fees (hasta 35%), acceso prioritario a subastas exclusivas, y participación en la gobernanza de la plataforma.',
  },
  {
    question: '¿Cómo sé si gané una subasta?',
    answer: 'Recibirás una notificación inmediata cuando ganes. El NFT se transferirá automáticamente a tu wallet y podrás ver la transacción en el dashboard. También recibirás una factura descargable con todos los detalles.',
  },
  {
    question: '¿Puedo cancelar una puja?',
    answer: 'En subastas English y Candle, las pujas son vinculantes y no se pueden cancelar. En subastas Vickrey, puedes modificar tu puja sellada hasta que termine el período. En subastas Dutch, no hay pujas - solo compras al precio actual.',
  },
  {
    question: '¿Qué fees cobra la plataforma?',
    answer: 'TEMPLUM cobra un 2.5% del precio final al vendedor. Los compradores no pagan fees adicionales, solo el gas de la transacción. Los holders de NDH obtienen descuentos según su tier.',
  },
  {
    question: '¿Qué pasa si nadie puja en mi subasta?',
    answer: 'Si una subasta termina sin pujas (en English) o sin compradores (en Dutch), el NFT vuelve a tu wallet. Puedes volver a listarlo con otros parámetros si lo deseas.',
  },
  {
    question: '¿La plataforma es segura?',
    answer: 'Sí. Todos los contratos están auditados, las transacciones son on-chain y verificables en Polygonscan. Los fondos nunca pasan por nuestra custodia - van directamente de comprador a vendedor.',
  },
];

const HowItWorksPage: React.FC = () => {
  const [selectedAuction, setSelectedAuction] = useState(auctionTypes[0]);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <LightBulbIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Cómo <span className="gradient-text">Funciona</span>
            </h1>
            <p className="text-xl text-gray-400 mb-8">
              Aprende todo lo que necesitas saber para participar en subastas descentralizadas
            </p>
          </motion.div>
        </div>
      </section>

      {/* Proceso paso a paso */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Empieza en 4 Pasos
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="relative"
                  >
                    {/* Conector */}
                    {idx < steps.length - 1 && (
                      <div className="hidden lg:block absolute top-12 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-primary-500/50 to-transparent" />
                    )}
                    <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 h-full">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center">
                          <Icon className="w-6 h-6 text-primary-400" />
                        </div>
                        <span className="text-4xl font-bold text-dark-600">{step.number}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                      <p className="text-gray-400 text-sm">{step.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Tipos de Subasta */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">4 Tipos de Subasta</h2>
              <p className="text-gray-400">Cada tipo tiene sus propias reglas y estrategias</p>
            </div>

            {/* Selector de tipo */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {auctionTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setSelectedAuction(type)}
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all ${
                      selectedAuction.id === type.id
                        ? `bg-gradient-to-r ${type.color} text-white`
                        : 'bg-dark-800 text-gray-400 hover:text-white hover:bg-dark-700'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {type.nameEs}
                  </button>
                );
              })}
            </div>

            {/* Detalle del tipo seleccionado */}
            <motion.div
              key={selectedAuction.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-dark-800/50 rounded-2xl border border-dark-700 overflow-hidden"
            >
              <div className={`p-6 bg-gradient-to-r ${selectedAuction.color}`}>
                <div className="flex items-center gap-3">
                  <selectedAuction.icon className="w-8 h-8 text-white" />
                  <div>
                    <h3 className="text-2xl font-bold text-white">{selectedAuction.nameEs}</h3>
                    <p className="text-white/80">{selectedAuction.name}</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <p className="text-lg text-gray-300 mb-6">{selectedAuction.description}</p>

                <div className="grid md:grid-cols-2 gap-8">
                  {/* Cómo funciona */}
                  <div>
                    <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <ClockIcon className="w-5 h-5 text-primary-400" />
                      Cómo Funciona
                    </h4>
                    <ol className="space-y-3">
                      {selectedAuction.howItWorks.map((step, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className="w-6 h-6 bg-primary-500/20 rounded-full flex items-center justify-center text-primary-400 text-sm flex-shrink-0">
                            {idx + 1}
                          </span>
                          <span className="text-gray-300">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Pros y Cons */}
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                        <CheckCircleIcon className="w-5 h-5 text-green-400" />
                        Ventajas
                      </h4>
                      <ul className="space-y-2">
                        {selectedAuction.pros.map((pro, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-gray-300">
                            <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                            {pro}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                        <QuestionMarkCircleIcon className="w-5 h-5 text-yellow-400" />
                        Consideraciones
                      </h4>
                      <ul className="space-y-2">
                        {selectedAuction.cons.map((con, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-gray-300">
                            <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full" />
                            {con}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Mejor para y ejemplo */}
                <div className="mt-8 pt-6 border-t border-dark-700 grid md:grid-cols-2 gap-6">
                  <div className="bg-dark-900/50 rounded-xl p-4">
                    <h4 className="text-white font-semibold mb-2">Mejor Para</h4>
                    <p className="text-gray-400 text-sm">{selectedAuction.bestFor}</p>
                  </div>
                  <div className="bg-dark-900/50 rounded-xl p-4">
                    <h4 className="text-white font-semibold mb-2">Ejemplo</h4>
                    <p className="text-gray-400 text-sm">{selectedAuction.example}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Beneficios de NDH */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Beneficios de Stakear NDH</h2>
              <p className="text-gray-400">Obtén ventajas exclusivas en la plataforma</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { tier: 'Bronze', stake: '1,000 NDH', discount: '10%', color: 'from-amber-700 to-amber-600' },
                { tier: 'Silver', stake: '10,000 NDH', discount: '20%', color: 'from-slate-400 to-slate-300' },
                { tier: 'Gold', stake: '50,000 NDH', discount: '30%', color: 'from-yellow-400 to-amber-500' },
                { tier: 'Platinum', stake: '100,000 NDH', discount: '35%', color: 'from-slate-300 to-indigo-400' },
              ].map((item, idx) => (
                <motion.div
                  key={item.tier}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-dark-800/50 rounded-xl border border-dark-700 overflow-hidden"
                >
                  <div className={`p-4 bg-gradient-to-r ${item.color}`}>
                    <h3 className="text-lg font-bold text-white">{item.tier}</h3>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-400 text-sm mb-2">Requiere</p>
                    <p className="text-white font-semibold mb-4">{item.stake}</p>
                    <p className="text-gray-400 text-sm mb-2">Descuento en fees</p>
                    <p className="text-2xl font-bold text-primary-400">{item.discount}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Preguntas Frecuentes</h2>
              <p className="text-gray-400">Respuestas a las dudas más comunes</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-dark-800/50 rounded-xl border border-dark-700 overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left"
                  >
                    <span className="text-white font-medium">{faq.question}</span>
                    <ArrowRightIcon className={`w-5 h-5 text-gray-400 transition-transform ${
                      expandedFaq === idx ? 'rotate-90' : ''
                    }`} />
                  </button>
                  {expandedFaq === idx && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="px-6 pb-4"
                    >
                      <p className="text-gray-400">{faq.answer}</p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-primary-500/20 to-purple-500/20 rounded-2xl p-8 md:p-12 border border-primary-500/30 text-center">
              <ShieldCheckIcon className="w-16 h-16 text-primary-400 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-white mb-4">
                ¿Listo para Empezar?
              </h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                Conecta tu wallet y explora las subastas activas. Tu primera puja está a solo unos clicks de distancia.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/live"
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors flex items-center gap-2"
                >
                  <FireIcon className="w-5 h-5" />
                  Ver Subastas en Vivo
                </Link>
                <Link
                  to="/explore"
                  className="px-8 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-xl transition-colors"
                >
                  Explorar Galería
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
